import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { sequencesApi, templatesApi, smsTemplatesApi, lineTemplatesApi, campaignsApi } from "@/api";
import type { SequenceTriggerType, Channel } from "@/types";
import { SEQUENCE_TRIGGER_LABELS, CHANNEL_LABELS } from "@/types";
import { ArrowLeft, Plus, Trash2, Save, Sparkles, Mail, MessageSquare, MessagesSquare, FileText } from "lucide-react";

interface StepForm {
  stepNumber: number;
  delayDays: number;
  channel: Channel;
  emailTemplateId: string;
  smsTemplateId: string;
  lineTemplateId: string;
  faxDocumentId: string;
  subject: string;
  condition: { branch: "opened" | "clicked" | "none"; ifTrue?: number; ifFalse?: number } | null;
}

const TRIGGER_TYPES: SequenceTriggerType[] = [
  "MANUAL", "ON_CAMPAIGN_OPEN", "ON_CAMPAIGN_CLICK",
  "ON_WEB_EVENT", "ON_SCORE_THRESHOLD", "ON_INACTIVITY",
];

export default function SequenceEditor() {
  const { id } = useParams();
  const isEdit = !!id && id !== "new";
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [triggerType, setTriggerType] = useState<SequenceTriggerType>("MANUAL");
  const [triggerConfig, setTriggerConfig] = useState<Record<string, unknown>>({});
  const [steps, setSteps] = useState<StepForm[]>([
    { stepNumber: 1, delayDays: 1, channel: "EMAIL", emailTemplateId: "", smsTemplateId: "", lineTemplateId: "", faxDocumentId: "", subject: "", condition: null },
  ]);

  // AI generation
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState("");

  // Test send
  const [testEmail, setTestEmail] = useState("");
  const [testStep, setTestStep] = useState(1);
  const [testStatus, setTestStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");

  const { data: templates } = useQuery({
    queryKey: ["templates"],
    queryFn: () => templatesApi.list(),
  });

  const { data: smsTemplates } = useQuery({
    queryKey: ["sms-templates"],
    queryFn: () => smsTemplatesApi.list(),
  });

  const { data: lineTemplates } = useQuery({
    queryKey: ["line-templates"],
    queryFn: () => lineTemplatesApi.list(),
  });

  const { data: campaigns } = useQuery({
    queryKey: ["campaigns"],
    queryFn: () => campaignsApi.list(),
  });

  const { data: existing } = useQuery({
    queryKey: ["sequence", id],
    queryFn: () => sequencesApi.get(id!),
    enabled: isEdit,
  });

  useEffect(() => {
    if (existing) {
      setName(existing.name);
      setDescription(existing.description || "");
      setTriggerType(existing.triggerType);
      setTriggerConfig(existing.triggerConfig || {});
      if (existing.steps && existing.steps.length > 0) {
        setSteps(existing.steps.map((s) => ({
          stepNumber: s.stepNumber,
          delayDays: s.delayDays,
          channel: (s.channel || "EMAIL") as Channel,
          emailTemplateId: s.emailTemplateId || "",
          smsTemplateId: s.smsTemplateId || "",
          lineTemplateId: s.lineTemplateId || "",
          faxDocumentId: s.faxDocumentId ? String(s.faxDocumentId) : "",
          subject: s.subject || "",
          condition: s.condition,
        })));
      }
    }
  }, [existing]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name,
        triggerType,
        triggerConfig: Object.keys(triggerConfig).length > 0 ? triggerConfig : null,
        description: description || null,
        steps: steps.map((s) => ({
          stepNumber: s.stepNumber,
          delayDays: s.delayDays,
          channel: s.channel,
          emailTemplateId: s.channel === "EMAIL" ? s.emailTemplateId || null : null,
          smsTemplateId: s.channel === "SMS" ? s.smsTemplateId || null : null,
          lineTemplateId: s.channel === "LINE" ? s.lineTemplateId || null : null,
          faxDocumentId: s.channel === "FAX" && s.faxDocumentId ? parseInt(s.faxDocumentId) : null,
          subject: s.subject || null,
          condition: s.condition?.branch === "none" ? null : s.condition,
        })),
      };
      if (isEdit) {
        return sequencesApi.update(id!, payload);
      }
      return sequencesApi.create(payload);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["sequences"] });
      navigate(`/sequences/${data.id}`);
    },
  });

  const testSendMutation = useMutation({
    mutationFn: () => sequencesApi.testSend({ sequenceId: id!, stepNumber: testStep, email: testEmail }),
    onSuccess: () => setTestStatus("sent"),
    onError: () => setTestStatus("error"),
    onMutate: () => setTestStatus("sending"),
  });

  const addStep = () => {
    const nextNum = steps.length > 0 ? Math.max(...steps.map((s) => s.stepNumber)) + 1 : 1;
    setSteps([...steps, { stepNumber: nextNum, delayDays: 1, channel: "EMAIL", emailTemplateId: "", smsTemplateId: "", lineTemplateId: "", faxDocumentId: "", subject: "", condition: null }]);
  };

  const removeStep = (idx: number) => {
    if (steps.length <= 1) return;
    const updated = steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, stepNumber: i + 1 }));
    setSteps(updated);
  };

  const updateStep = (idx: number, field: string, value: unknown) => {
    const updated = [...steps];
    (updated[idx] as any)[field] = value;
    setSteps(updated);
  };

  const generateWithAi = async () => {
    if (!aiPrompt.trim() || aiGenerating) return;
    setAiGenerating(true);
    setAiError("");
    try {
      const result = await sequencesApi.generate(aiPrompt.trim());
      if (result.name) setName(result.name);
      if (result.description) setDescription(result.description);
      if (result.triggerType) setTriggerType(result.triggerType as SequenceTriggerType);
      if (result.steps && result.steps.length > 0) {
        setSteps(result.steps.map((s) => ({
          stepNumber: s.stepNumber,
          delayDays: s.delayDays,
          channel: "EMAIL" as Channel,
          emailTemplateId: "",
          smsTemplateId: "",
          lineTemplateId: "",
          faxDocumentId: "",
          subject: s.subject,
          condition: s.condition ? {
            branch: s.condition.branch as "opened" | "clicked",
            ifTrue: s.condition.ifTrue,
            ifFalse: s.condition.ifFalse,
          } : null,
        })));
      }
    } catch (err: any) {
      setAiError(err.message || "AI生成に失敗しました");
    } finally {
      setAiGenerating(false);
    }
  };

  const canSave = name.trim() && steps.every((s) => {
    if (s.channel === "SMS") return !!s.smsTemplateId;
    if (s.channel === "LINE") return !!s.lineTemplateId;
    if (s.channel === "FAX") return !!s.faxDocumentId;
    return !!s.emailTemplateId;
  });

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1 hover:bg-slate-100 rounded">
          <ArrowLeft size={20} />
        </button>
        <h2 className="text-xl font-bold">{isEdit ? "シーケンス編集" : "新規シーケンス"}</h2>
      </div>

      {/* AI generation */}
      <div className="bg-violet-50 rounded-lg border border-violet-200 p-6 space-y-3">
        <label className="flex items-center gap-1 font-bold text-sm text-violet-700">
          <Sparkles size={16} /> AIでシーケンスを生成
        </label>
        <p className="text-xs text-violet-600">
          やりたいことを自由に記述してください。名前・ステップ・件名を自動生成します。テンプレートは手動で選択してください。
        </p>
        <textarea
          value={aiPrompt}
          onChange={(e) => setAiPrompt(e.target.value)}
          rows={3}
          className="w-full border border-violet-200 rounded-md px-3 py-2 text-sm placeholder:text-violet-300"
          placeholder="例: 新規リードへのウェルカムシーケンス。初回は自己紹介と製品カタログの案内、3日後に導入事例、7日後にお問い合わせ促進。開封しなかった人にはリマインドを送る。"
        />
        <div className="flex items-center gap-3">
          <button
            onClick={generateWithAi}
            disabled={!aiPrompt.trim() || aiGenerating}
            className="flex items-center gap-1 text-sm px-4 py-2 bg-violet-600 text-white rounded-md hover:bg-violet-700 disabled:opacity-50 transition-colors"
          >
            <Sparkles size={14} />
            {aiGenerating ? "生成中..." : "生成する"}
          </button>
          {aiError && <span className="text-sm text-red-600">{aiError}</span>}
        </div>
      </div>

      {/* Basic info */}
      <div className="bg-white rounded-lg shadow-sm border p-6 space-y-4">
        <h3 className="font-bold text-sm text-slate-700">基本情報</h3>
        <div>
          <label className="block text-sm font-medium text-slate-600 mb-1">名前</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full border rounded-md px-3 py-2 text-sm"
            placeholder="ウェルカムシーケンス"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-600 mb-1">説明</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full border rounded-md px-3 py-2 text-sm"
            rows={2}
            placeholder="新規リードへの初回フォローアップ"
          />
        </div>
      </div>

      {/* Trigger */}
      <div className="bg-white rounded-lg shadow-sm border p-6 space-y-4">
        <h3 className="font-bold text-sm text-slate-700">トリガー設定</h3>
        <div>
          <label className="block text-sm font-medium text-slate-600 mb-1">トリガータイプ</label>
          <select
            value={triggerType}
            onChange={(e) => {
              setTriggerType(e.target.value as SequenceTriggerType);
              setTriggerConfig({});
            }}
            className="w-full border rounded-md px-3 py-2 text-sm"
          >
            {TRIGGER_TYPES.map((t) => (
              <option key={t} value={t}>{SEQUENCE_TRIGGER_LABELS[t]}</option>
            ))}
          </select>
        </div>

        {(triggerType === "ON_CAMPAIGN_OPEN" || triggerType === "ON_CAMPAIGN_CLICK") && (
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">対象キャンペーン</label>
            <select
              value={(triggerConfig.campaignId as string) || ""}
              onChange={(e) => setTriggerConfig({ ...triggerConfig, campaignId: e.target.value })}
              className="w-full border rounded-md px-3 py-2 text-sm"
            >
              <option value="">選択してください</option>
              {campaigns?.data.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        )}

        {triggerType === "ON_WEB_EVENT" && (
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">URLパターン（部分一致）</label>
            <input
              value={(triggerConfig.urlPattern as string) || ""}
              onChange={(e) => setTriggerConfig({ ...triggerConfig, urlPattern: e.target.value })}
              className="w-full border rounded-md px-3 py-2 text-sm"
              placeholder="/product/rizing"
            />
          </div>
        )}

        {triggerType === "ON_SCORE_THRESHOLD" && (
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">スコア閾値</label>
            <input
              type="number"
              value={(triggerConfig.scoreThreshold as number) || ""}
              onChange={(e) => setTriggerConfig({ ...triggerConfig, scoreThreshold: parseInt(e.target.value) || 0 })}
              className="w-full border rounded-md px-3 py-2 text-sm"
              placeholder="30"
            />
          </div>
        )}

        {triggerType === "ON_INACTIVITY" && (
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">非活動日数</label>
            <input
              type="number"
              value={(triggerConfig.inactiveDays as number) || ""}
              onChange={(e) => setTriggerConfig({ ...triggerConfig, inactiveDays: parseInt(e.target.value) || 0 })}
              className="w-full border rounded-md px-3 py-2 text-sm"
              placeholder="60"
            />
          </div>
        )}
      </div>

      {/* Steps */}
      <div className="bg-white rounded-lg shadow-sm border p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-sm text-slate-700">ステップ</h3>
          <button
            onClick={addStep}
            className="flex items-center gap-1 text-sm text-blue-500 hover:text-blue-700"
          >
            <Plus size={14} /> ステップ追加
          </button>
        </div>

        <div className="space-y-4">
          {steps.map((step, idx) => (
            <div key={idx} className="border rounded-lg p-4 space-y-3 bg-slate-50">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700">Step {step.stepNumber}</span>
                {steps.length > 1 && (
                  <button onClick={() => removeStep(idx)} className="text-red-400 hover:text-red-600">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>

              {/* Channel selector */}
              <div className="flex gap-1 mb-1">
                {(["EMAIL", "SMS", "LINE", "FAX"] as Channel[]).map((ch) => {
                  const Icon = ch === "EMAIL" ? Mail : ch === "SMS" ? MessageSquare : ch === "LINE" ? MessagesSquare : FileText;
                  const active = step.channel === ch;
                  return (
                    <button
                      key={ch}
                      type="button"
                      onClick={() => updateStep(idx, "channel", ch)}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                        active
                          ? ch === "EMAIL" ? "bg-blue-100 text-blue-700" : ch === "SMS" ? "bg-green-100 text-green-700" : ch === "LINE" ? "bg-[#E8F5E9] text-[#06C755]" : "bg-orange-100 text-orange-700"
                          : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                      }`}
                    >
                      <Icon size={12} />
                      {CHANNEL_LABELS[ch]}
                    </button>
                  );
                })}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">待機日数</label>
                  <input
                    type="number"
                    min={0}
                    value={step.delayDays}
                    onChange={(e) => updateStep(idx, "delayDays", parseInt(e.target.value) || 0)}
                    className="w-full border rounded px-2 py-1.5 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">
                    {step.channel === "FAX" ? "FAX Document ID" : "テンプレート"}
                  </label>
                  {step.channel === "EMAIL" ? (
                    <select
                      value={step.emailTemplateId}
                      onChange={(e) => updateStep(idx, "emailTemplateId", e.target.value)}
                      className="w-full border rounded px-2 py-1.5 text-sm"
                    >
                      <option value="">選択</option>
                      {templates?.data.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  ) : step.channel === "SMS" ? (
                    <select
                      value={step.smsTemplateId}
                      onChange={(e) => updateStep(idx, "smsTemplateId", e.target.value)}
                      className="w-full border rounded px-2 py-1.5 text-sm"
                    >
                      <option value="">選択</option>
                      {smsTemplates?.data.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  ) : step.channel === "LINE" ? (
                    <select
                      value={step.lineTemplateId}
                      onChange={(e) => updateStep(idx, "lineTemplateId", e.target.value)}
                      className="w-full border rounded px-2 py-1.5 text-sm"
                    >
                      <option value="">選択</option>
                      {lineTemplates?.data.map((t) => (
                        <option key={t.id} value={t.id}>{t.name} ({t.type})</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="number"
                      value={step.faxDocumentId}
                      onChange={(e) => updateStep(idx, "faxDocumentId", e.target.value)}
                      className="w-full border rounded px-2 py-1.5 text-sm"
                      placeholder="ICTFax Document ID"
                    />
                  )}
                </div>
              </div>

              {step.channel === "EMAIL" && (
                <div>
                  <label className="block text-xs text-slate-500 mb-1">件名（上書き、空欄でテンプレートの件名）</label>
                  <input
                    value={step.subject}
                    onChange={(e) => updateStep(idx, "subject", e.target.value)}
                    className="w-full border rounded px-2 py-1.5 text-sm"
                    placeholder="テンプレートの件名を使用"
                  />
                </div>
              )}

              {step.channel === "EMAIL" && (<>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">分岐条件</label>
                  <select
                    value={step.condition?.branch || "none"}
                    onChange={(e) => {
                      const branch = e.target.value as "opened" | "clicked" | "none";
                      if (branch === "none") {
                        updateStep(idx, "condition", null);
                      } else {
                        updateStep(idx, "condition", { branch });
                      }
                    }}
                    className="w-full border rounded px-2 py-1.5 text-sm"
                  >
                    <option value="none">なし（次のステップへ）</option>
                    <option value="opened">前ステップ開封で分岐</option>
                    <option value="clicked">前ステップクリックで分岐</option>
                  </select>
                </div>

                {step.condition && step.condition.branch !== "none" && (
                  <div className="grid grid-cols-2 gap-3 pl-4 border-l-2 border-blue-200">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">
                        {step.condition.branch === "opened" ? "開封あり" : "クリックあり"} → Step#
                      </label>
                      <input
                        type="number"
                        min={1}
                        value={step.condition.ifTrue ?? ""}
                        onChange={(e) => updateStep(idx, "condition", {
                          ...step.condition,
                          ifTrue: parseInt(e.target.value) || undefined,
                        })}
                        className="w-full border rounded px-2 py-1.5 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">
                        {step.condition.branch === "opened" ? "未開封" : "未クリック"} → Step#
                      </label>
                      <input
                        type="number"
                        min={1}
                        value={step.condition.ifFalse ?? ""}
                        onChange={(e) => updateStep(idx, "condition", {
                          ...step.condition,
                          ifFalse: parseInt(e.target.value) || undefined,
                        })}
                        className="w-full border rounded px-2 py-1.5 text-sm"
                      />
                    </div>
                  </div>
                )}
              </>)}
            </div>
          ))}
        </div>
      </div>

      {/* Test send (edit mode only) */}
      {isEdit && (
        <div className="bg-white rounded-lg shadow-sm border p-6 space-y-4">
          <h3 className="font-bold text-sm text-slate-700">テスト配信</h3>
          <p className="text-xs text-slate-500">
            任意のメールアドレスにステップのテストメールを送信します。マージ変数はテストデータに置換されます。
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">メールアドレス *</label>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => { setTestEmail(e.target.value); setTestStatus("idle"); }}
                className="w-full border rounded-md px-3 py-2 text-sm"
                placeholder="test@example.com"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">ステップ</label>
              <select
                value={testStep}
                onChange={(e) => { setTestStep(parseInt(e.target.value)); setTestStatus("idle"); }}
                className="w-full border rounded-md px-3 py-2 text-sm"
              >
                {steps.map((s) => (
                  <option key={s.stepNumber} value={s.stepNumber}>
                    Step {s.stepNumber}{s.subject ? ` — ${s.subject}` : ""}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => testSendMutation.mutate()}
              disabled={!testEmail || testSendMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-50 transition-colors text-sm"
            >
              <Mail size={16} />
              {testSendMutation.isPending ? "送信中..." : "テスト送信"}
            </button>
            {testStatus === "sent" && (
              <span className="text-sm text-green-600">テストメールを送信しました</span>
            )}
            {testStatus === "error" && (
              <span className="text-sm text-red-600">送信に失敗しました</span>
            )}
          </div>
        </div>
      )}

      {/* Save */}
      <div className="flex gap-3">
        <button
          onClick={() => saveMutation.mutate()}
          disabled={!canSave || saveMutation.isPending}
          className="flex items-center gap-2 px-6 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600 disabled:opacity-50"
        >
          <Save size={16} />
          {saveMutation.isPending ? "保存中..." : "保存"}
        </button>
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 border rounded-md text-sm hover:bg-slate-50"
        >
          キャンセル
        </button>
      </div>

      {saveMutation.isError && (
        <p className="text-sm text-red-600">保存に失敗しました</p>
      )}
    </div>
  );
}
